/**
 * GMA-1995 / GMA-1996 — elopement boundary API client.
 *
 * Proposed contract (backend not shipped yet — GMA-1996):
 *   GET    /api/v1/clients/:clientId/elopement-location
 *   PUT    /api/v1/clients/:clientId/elopement-location
 *   DELETE /api/v1/clients/:clientId/elopement-location
 *
 * Body (PUT): GeoJSON Polygon
 *   { "type": "Polygon", "coordinates": [[[lng, lat], ...closed]] }
 *
 * Read semantics: confirmed 404 → null (no saved boundary). Any other
 * non-2xx, network, or malformed body throws so the UI cannot treat a
 * failed load as an empty fence and overwrite it. Save/delete throw a
 * user-facing Error without inventing success. No localStorage of rings.
 */
import { apiRequest } from "./baseApi";
import {
  latLngsFromGeoJsonPolygon,
  toGeoJsonRing,
  validateClosedElopementRing,
} from "@graymattersalliance/compass-map";

const pathFor = (clientId) => {
  const id = String(clientId ?? "").trim();
  if (!id) {
    throw new Error("Client id is required to load an elopement boundary.");
  }
  return `clients/${encodeURIComponent(id)}/elopement-location`;
};

const loadFailedError = () =>
  new Error("Could not load the saved boundary. Nothing was changed.");

/**
 * @param {string|number} clientId
 * @returns {Promise<Array<[number, number]>|null>} closed GeoJSON ring or null
 */
export async function fetchElopementLocation(clientId) {
  const res = await apiRequest(pathFor(clientId), {
    method: "GET",
    version: "v1",
  });
  if (res && res.status === 404) return null;
  if (!res || !res.ok) throw loadFailedError();
  const body = await res.json().catch(() => null);
  if (!body) throw loadFailedError();
  const geometry =
    body.type === "Polygon"
      ? body
      : body.elopementLocation && body.elopementLocation.type === "Polygon"
        ? body.elopementLocation
        : null;
  if (!geometry) throw loadFailedError();
  const latLngs = latLngsFromGeoJsonPolygon(geometry);
  const ring = toGeoJsonRing(latLngs, { requireClosed: true });
  if (!ring) throw loadFailedError();
  return ring;
}

/**
 * @param {string|number} clientId
 * @param {Array<[number, number]|{lat:number,lng:number}>} ring closed ring
 */
export async function saveElopementLocation(clientId, ring) {
  const geo = toGeoJsonRing(ring, { requireClosed: true });
  if (!geo || validateClosedElopementRing(geo)) {
    throw new Error(
      "The boundary must be a closed shape with at least three corners.",
    );
  }
  const res = await apiRequest(pathFor(clientId), {
    method: "PUT",
    version: "v1",
    body: {
      type: "Polygon",
      coordinates: [geo],
    },
  });
  if (!res || !res.ok) {
    if (res && res.status === 404) {
      throw new Error(
        "Saving the boundary is not available yet (backend endpoint pending).",
      );
    }
    throw new Error(
      "Could not save the elopement boundary. Nothing was changed.",
    );
  }
  return true;
}

/**
 * @param {string|number} clientId
 */
export async function deleteElopementLocation(clientId) {
  const res = await apiRequest(pathFor(clientId), {
    method: "DELETE",
    version: "v1",
  });
  if (!res || !res.ok) {
    if (res && res.status === 404) {
      throw new Error(
        "Clearing the boundary is not available yet (backend endpoint pending).",
      );
    }
    throw new Error(
      "Could not clear the elopement boundary. Nothing was changed.",
    );
  }
  return true;
}
